import { Component, computed, effect, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextarea } from 'primeng/inputtextarea';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { NotificationService } from 'src/app/core/services/notification.service';
import { CompanyService } from '../../services/company.service';
import { PackService } from '../../services/pack.service';
import {
  MailingListEntry,
  MailingListFilters,
  ExportFormat,
  LANGUAGE_OPTIONS,
} from '../../models/mailing-list.model';

@Component({
  selector: 'app-mailing-list',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TableModule,
    ToastModule,
    CheckboxModule,
    DatePickerModule,
    InputTextarea,
    RadioButtonModule,
    ToggleSwitchModule,
    MultiSelectModule,
    TooltipModule,
    TagModule,
  ],
  templateUrl: './mailing-list.component.html',
})
export class MailingListComponent implements OnInit {
  companyService = inject(CompanyService);
  notificationService = inject(NotificationService);
  packService = inject(PackService);

  constructor() {
    // Packs are loaded asynchronously: select them all once they arrive
    effect(() => {
      const options = this.packOptions();
      if (options.length && !this.packsInitialized) {
        this.packsInitialized = true;
        this.selectedPacks = options.map((o) => o.value);
      }
    });
  }

  ngOnInit(): void {
    this.loadMailingList();
  }

  // Filter options
  languageOptions = LANGUAGE_OPTIONS;
  packOptions = computed(() =>
    (this.packService.packs() ?? []).map((pack) => ({
      label: pack.name,
      value: pack.name,
    })),
  );
  private packsInitialized = false;

  // Filter values - all selected by default
  selectedLanguages: string[] = LANGUAGE_OPTIONS.map((o) => o.value);
  selectedPacks: string[] = [];
  registeredAfter: Date | null = null;
  registeredBefore: Date | null = null;
  lastLoginAfter: Date | null = null;
  lastLoginBefore: Date | null = null;
  includeOptedOut = false;
  adminsOnly = false;

  // Results
  entries: MailingListEntry[] = [];
  selectedEntries: MailingListEntry[] = [];
  loading = false;

  // Export
  exportFormat: ExportFormat = 'semicolon';
  exportedEmails = '';

  loadMailingList(): void {
    this.loading = true;

    const filters: MailingListFilters = {
      languages: this.selectedLanguages.length
        ? this.selectedLanguages
        : undefined,
      packs: this.selectedPacks.length ? this.selectedPacks : undefined,
      registeredAfter: this.registeredAfter ?? undefined,
      registeredBefore: this.registeredBefore ?? undefined,
      lastLoginAfter: this.lastLoginAfter ?? undefined,
      lastLoginBefore: this.lastLoginBefore ?? undefined,
      includeOptedOut: this.includeOptedOut,
      adminsOnly: this.adminsOnly,
    };

    this.companyService.getMailingList(filters).subscribe({
      next: (response) => {
        this.entries = response.data;
        this.selectedEntries = [...response.data]; // Select all by default
        this.updateExportedEmails();
        this.loading = false;
      },
      error: () => {
        this.notificationService.error('Failed to load mailing list');
        this.loading = false;
      },
    });
  }

  resetFilters(): void {
    this.selectedLanguages = LANGUAGE_OPTIONS.map((o) => o.value);
    this.selectedPacks = this.packOptions().map((o) => o.value);
    this.registeredAfter = null;
    this.registeredBefore = null;
    this.lastLoginAfter = null;
    this.lastLoginBefore = null;
    this.includeOptedOut = false;
    this.adminsOnly = false;
    this.entries = [];
    this.selectedEntries = [];
    this.exportedEmails = '';
  }

  selectAll(): void {
    this.selectedEntries = [...this.entries];
    this.updateExportedEmails();
  }

  deselectAll(): void {
    this.selectedEntries = [];
    this.updateExportedEmails();
  }

  onSelectionChange(): void {
    this.updateExportedEmails();
  }

  onExportFormatChange(): void {
    this.updateExportedEmails();
  }

  updateExportedEmails(): void {
    const emails = this.selectedEntries.map((e) => e.email);

    switch (this.exportFormat) {
      case 'semicolon':
        this.exportedEmails = emails.join('; ');
        break;
      case 'newline':
        this.exportedEmails = emails.join('\n');
        break;
      case 'comma':
        this.exportedEmails = emails.join(', ');
        break;
    }
  }

  copyToClipboard(): void {
    navigator.clipboard.writeText(this.exportedEmails).then(() => {
      this.notificationService.success(
        `${this.selectedEntries.length} email(s) copied to clipboard`
      );
    });
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  getLanguageLabel(code: string): string {
    const labels: Record<string, string> = {
      en: 'EN',
      fr: 'FR',
      de: 'DE',
      nl: 'NL',
    };
    return labels[code] || code;
  }
}
